import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useBackgroundContext } from '../../contexts/BackgroundContext';
import { useThemeContext } from '../../contexts/ThemeContext';
import { logout } from '../../services/authService';
import { getInFlightCount } from '../../services/processService';
import type { PipelineHealth } from '../../services/processService';
import { usePollingResource } from '../../hooks/usePollingResource';
import { getFirstName } from '../../utils/auth';
import { formatDateTime, formatRelativeTime } from '../../utils/formatters';
import { AlertIcon, ListIcon, RefreshIcon } from '../icons';
import { BackgroundToggle } from '../background/BackgroundToggle';
import { UnaLogo } from '../brand/UnaLogo';
import { ThemeToggle } from '../theme/ThemeToggle';
import { cx } from '../../utils/cx';

function NavIcon({ type }: { type: 'dashboard' | 'list' | 'upload' }) {
  if (type === 'list') {
    return <ListIcon />;
  }

  if (type === 'upload') {
    return (
      <svg aria-hidden="true" viewBox="0 0 24 24">
        <path d="M12 16V5" />
        <path d="m7.8 9.2 4.2-4.2 4.2 4.2" />
        <path d="M5 18.8h14" />
      </svg>
    );
  }

  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <path d="M5 5h5v5H5z" />
      <path d="M14 5h5v5h-5z" />
      <path d="M5 14h5v5H5z" />
      <path d="M14 14h5v5h-5z" />
    </svg>
  );
}

// The pipeline runs every 10 minutes. Three runs missed in a row means something
// is wrong (dead worker or scheduler, expired Graph secret) and the operator must
// be told: automated, the failure mode is silent, and no other screen shows it.
const PIPELINE_STALE_AFTER_MS = 30 * 60 * 1000;

type PipelineIndicatorState =
  | { kind: 'running'; invoiceCount: number }
  | { kind: 'alert'; label: string; since: string | null; title: string }
  | { kind: 'idle'; processedAt: string | null; title: string };

function invoiceNoun(count: number): string {
  return count === 1 ? 'fatura' : 'faturas';
}

/**
 * Which of the pipeline's states to show. Runs are frequent and mostly empty, so
 * when the last one ran says little while all is well: the healthy state shows
 * when an invoice was last handled, and the run itself only surfaces when it is
 * in progress or something is wrong.
 */
function getPipelineIndicatorState(health: PipelineHealth, now: number): PipelineIndicatorState {
  const { count, last_run: lastRun, last_processed_at: processedAt } = health;

  if (!lastRun) {
    return {
      kind: 'alert',
      label: 'Pipeline sem execuções',
      since: null,
      title: 'O pipeline ainda não executou.',
    };
  }

  if (lastRun.status === 'running') {
    // Taken from the run itself, not from the in-flight count: processes can wait
    // for the next run, and a spinner with nothing running would be a lie.
    return lastRun.stalled
      ? {
          kind: 'alert',
          label: 'Execução bloqueada',
          since: lastRun.started_at,
          title: 'A execução do pipeline não terminou dentro do tempo esperado.',
        }
      : { kind: 'running', invoiceCount: count };
  }

  const finishedAt = lastRun.finished_at ?? lastRun.started_at;

  if (lastRun.status === 'failed') {
    return {
      kind: 'alert',
      label: 'Pipeline com erro',
      since: finishedAt,
      title: 'A última execução do pipeline falhou.',
    };
  }

  if (now - new Date(finishedAt).getTime() > PIPELINE_STALE_AFTER_MS) {
    return {
      kind: 'alert',
      label: 'Pipeline sem execuções',
      since: finishedAt,
      title: 'Não há execuções do pipeline concluídas há mais de 30 minutos.',
    };
  }

  const title = [
    processedAt ? `Último processamento: ${formatDateTime(processedAt)}` : null,
    `Última execução: ${formatRelativeTime(finishedAt)}`,
    count > 0 ? `${count} ${invoiceNoun(count)} por concluir` : null,
  ]
    .filter(Boolean)
    .join('\n');

  return { kind: 'idle', processedAt, title };
}

/**
 * Pipeline health, and the only trace an operator has of it: in-flight processes
 * are hidden from every table, and a stalled scheduler looks exactly like a quiet
 * mailbox. Informative only — no click target.
 *
 * Only the state label is a live region. The relative time changes every minute
 * and would otherwise be announced to screen readers again and again.
 */
function ProcessingIndicator() {
  const { data, error } = usePollingResource({
    load: (signal) => getInFlightCount(signal),
  });

  if (error || !data) {
    // A wrong health indicator is worse than none.
    return null;
  }

  const state = getPipelineIndicatorState(data, Date.now());

  if (state.kind === 'running') {
    return (
      <span className="processing-indicator">
        <RefreshIcon />
        <span>
          <span role="status">A processar…</span>
          {state.invoiceCount > 0 ? (
            <>
              {' · '}
              <strong>{state.invoiceCount}</strong> {invoiceNoun(state.invoiceCount)}
            </>
          ) : null}
        </span>
      </span>
    );
  }

  if (state.kind === 'alert') {
    return (
      <span
        className={cx('processing-indicator', 'processing-indicator--idle', 'processing-indicator--stale')}
        title={state.title}
      >
        <AlertIcon />
        <span>
          <span role="status">{state.label}</span>
          {state.since ? (
            <>
              {' '}
              <strong>{formatRelativeTime(state.since)}</strong>
            </>
          ) : null}
        </span>
      </span>
    );
  }

  return (
    <span className="processing-indicator processing-indicator--idle" title={state.title}>
      <span>
        {state.processedAt ? (
          <>
            Último processamento <strong>{formatRelativeTime(state.processedAt)}</strong>
          </>
        ) : (
          'Sem processamentos ainda'
        )}
      </span>
    </span>
  );
}

function LogoutIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <path d="M14 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2v-2" />
      <path d="M10 12h10" />
      <path d="m17 9 3 3-3 3" />
    </svg>
  );
}

export function BackOfficeNavbar() {
  const { isDarkTheme, theme, toggleTheme } = useThemeContext();
  const { isAnimated, toggleAnimated } = useBackgroundContext();
  const location = useLocation();
  const navigate = useNavigate();
  const firstName = getFirstName() || 'Utilizador';
  const avatarInitial = firstName.trim().charAt(0).toUpperCase() || 'U';
  const isProcessDetailActive = /^\/processes\/[^/]+/.test(location.pathname);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isMenuOpen) {
      return undefined;
    }

    function handlePointerDown(event: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsMenuOpen(false);
      }
    }

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMenuOpen]);

  function handleLogout() {
    setIsMenuOpen(false);
    logout();
    navigate('/login', { replace: true });
  }

  return (
    <header className="relative grid grid-cols-[1fr_auto_1fr] items-start gap-[var(--bo-header-gap)]">
      <div className="backoffice-greeting">
        <span className="backoffice-greeting-dot" aria-hidden="true" />
        <span className="backoffice-greeting-text">
          Olá, <strong>{firstName}</strong>
        </span>
      </div>

      <div className="grid justify-items-center gap-md">
        <Link to="/dashboard" aria-label="Ir para o dashboard" className="text-inherit no-underline">
          <UnaLogo className="w-[var(--bo-logo-width)] text-accent object-contain" />
        </Link>

        <nav className="backoffice-icon-nav" aria-label="Navegação do backoffice">
          <NavLink
            className={({ isActive }) => cx('backoffice-icon-nav-item', isActive && 'active')}
            to="/dashboard"
          >
            <NavIcon type="dashboard" />
            <span>Dashboard</span>
          </NavLink>
          <NavLink
            className={({ isActive }) => cx('backoffice-icon-nav-item', isActive && 'active')}
            end
            to="/processes"
          >
            <NavIcon type="list" />
            <span>Processos</span>
          </NavLink>
          <span
            className={cx('backoffice-icon-nav-item', isProcessDetailActive && 'active')}
            aria-current={isProcessDetailActive ? 'page' : undefined}
          >
            <NavIcon type="upload" />
            <span>Processamento</span>
          </span>
        </nav>
      </div>

      <div className="backoffice-actions">
        <ProcessingIndicator />
        <BackgroundToggle isAnimated={isAnimated} onToggle={toggleAnimated} />
        <ThemeToggle isDarkTheme={isDarkTheme} onToggle={toggleTheme} theme={theme} />
        <div className="user-menu" ref={userMenuRef}>
          <button
            aria-expanded={isMenuOpen}
            aria-haspopup="menu"
            aria-label={`Conta de ${firstName}`}
            className="user-avatar"
            onClick={() => setIsMenuOpen((open) => !open)}
            type="button"
          >
            {avatarInitial}
          </button>

          {isMenuOpen ? (
            <div className="user-menu-popover" role="menu">
              <button className="user-menu-logout" onClick={handleLogout} role="menuitem" type="button">
                <LogoutIcon />
                Terminar Sessão
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </header>
  );
}
